import React, { FC, useContext, useEffect, useState } from "react";
import { NO_IMAGE } from "../../../../assets";
import { BannerSection, HeaderSection } from "../../../../models/manageWebSite";
import { MobXProviderContext } from "mobx-react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { FileUpload } from "primereact/fileupload";
import { getErrorTitle, WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES } from "../../../../constants/constants";
import { Button } from "primereact/button";
import { Controller, useForm } from "react-hook-form";
import { InputTextarea } from "primereact/inputtextarea";
import { ReferenceType } from "../../../../models/document";
import { normalizePlainText } from "../../../../utils/securityUtils";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";

interface EditModalProps {
  data: BannerSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const EditModal: FC<EditModalProps> = ({
  isOpen,
  toggleModal,
  flag,
  setFlag,
  data,
  homeId,
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loadingImg, setLoadingImg] = useState<boolean>(false);
  const [updatedData, setUpdatedData] = useState<BannerSection | null>(data);

  const defaultValues = {
    description: data?.description || "",
    logo: data?.sectionImg || "",
  }

  const { formState: { errors }, handleSubmit, reset, control } = useForm({ defaultValues });

  useEffect(() => {
    setUpdatedData(data);
    setImageUrl(data?.sectionSignedImg || null);
  }, [data, isOpen]);

  const handleFileChange = (event) => {
    if (event.files && event.files[0]) {
      const file = event.files[0];
      setSelectedFile(file);
      const url = URL.createObjectURL(file)
      setImageUrl(url);
      setUpdatedData((prev) =>
        prev
          ? { ...prev, sectionImg: `website/default_template/${homeId}/draft/BANNER-logo` }
          : prev,
      );
    }
  };

  const uploadProfileImage = async (objectKey: string) => {
    if (!imageUrl) return null;
    try {
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
        homeId!,
        ReferenceType.BUSINESS_HOME,
        objectKey,
      );
      await backendAPIStore.uploadFileToS3(presignedUrl, selectedFile);
      return presignedUrl.split("?")[0];
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      return null;
    }
  };

  const onSubmit = async (data: any) => {
    if (!imageUrl) {
      setErrorMessage("Please select an image before submitting.");
      return;
    }
    setErrorMessage(null);
    setLoadingImg(true);
    const objectKey = `website/default_template/${homeId}/draft/BANNER-logo`
    try {
      await backendAPIStore.init();
      let { sectionSignedImg, ...apiData } = {
        ...updatedData,
        modifiedAt: new Date().toISOString(),
        description: normalizePlainText(data.description, { multiline: true, maxLength: 500 }),
      };

      if (selectedFile) {
        await uploadProfileImage(objectKey);
        let { sectionSignedImg, ...apiData } = {
          ...updatedData,
          sectionImg: objectKey,
        };
      }

      await backendAPIStore.updateTemplateSection(apiData);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to upload image', life: 3000 });
    } finally {
      reset();
      toggleModal(false);
      setFlag(!flag);
      setSelectedFile(null);
      setLoadingImg(false);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = renderSurfaceHeader(
    "Edit Banner Section",
    "Refresh the homepage banner image and supporting description.",
    "pi pi-images",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading: loadingImg,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Section",
  });

  const chooseOptions = { icon: 'pi pi-fw pi-images', className: 'custom-choose-btn p-button-rounded p-button-outlined' };
  const cancelOptions = { icon: 'pi pi-fw pi-times', className: 'custom-cancel-btn p-button-rounded p-button-outlined p-button-danger' };

  const fileHeaderTemplate = (options) => {
    const { className, chooseButton, cancelButton } = options;

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {cancelButton}
      </div>
    );
  };

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <form>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Banner content</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Upload a cleaner visual and keep the top-of-page description aligned with the rest of the site.
          </div>
        </div>
        <div style={{ ...appFormSectionStyle, textAlign: "center" }}>
          <img src={imageUrl || NO_IMAGE} width="200" height="200" style={{ objectFit: "contain", borderRadius: "16px", marginBottom: "1rem" }} />
          <FileUpload mode="advanced" name="profileImage" onSelect={handleFileChange} accept="image/*" maxFileSize={WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES}
            headerTemplate={fileHeaderTemplate}
            chooseOptions={chooseOptions} cancelOptions={cancelOptions}
            emptyTemplate={<p className="m-0">Drag and drop image here to upload.</p>}
          />
        </div>
        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
          <label htmlFor="description">Description</label>
          <Controller name="description" control={control} defaultValue={data?.description || ""} render={({ field }) => (
            <InputTextarea id="description" rows={5} cols={20} style={{ width: "100%" }} {...field} className={`p-inputtext p-component ${errors.description ? 'p-invalid' : ''}`} />
          )} />
          {getFormErrorMessage('description')}
        </div>
      </form>
    </Dialog>
  );
};

export default EditModal;
